import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import KeralitOptionsPanel from '../components/KeralitOptionsPanel';
import KeralitDrawingBoard from '../components/KeralitDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  keralitProfileSystems,
  glassTextures, 
  glazingTypes, 
  colors, 
  woodJointOptions,
  PRICING,
  normalizeKeralitProfileSystem,
} from '../data/mockData';
import {
  applyPersistedFrameColors,
  savePersistedFrameColors,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const DEFAULT_KERALIT_CONFIG = {
  productType: 'keralit',
  width: 1500,
  height: 1500,
  profileSystem: 'potdeksel-190',
  interiorColor: 'anthracite',
  exteriorColor: 'anthracite',
  draaideelColor: 'anthracite',
  lineType: 'horizontal',
  lineSpacing: '19cm',
  quantity: 1,
};

const syncKeralitFrameColor = (config = {}) => {
  const color = config.exteriorColor || config.interiorColor || 'anthracite';
  return {
    ...config,
    exteriorColor: color,
    interiorColor: color,
    draaideelColor: color,
  };
};

const Keralit = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    syncKeralitFrameColor(
      withDefaultAdditionalItems({
        ...applyPersistedFrameColors(DEFAULT_KERALIT_CONFIG, DOOR_COLOR_FIELDS),
        profileSystem: normalizeKeralitProfileSystem(DEFAULT_KERALIT_CONFIG.profileSystem),
      })
    );

  const { config, setConfig, navLoadToken, lastNavLoadedConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) =>
      syncKeralitFrameColor(
        withDefaultAdditionalItems({
          ...loaded,
          profileSystem: normalizeKeralitProfileSystem(loaded?.profileSystem),
        })
      )
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  useEffect(() => {
    if (navLoadToken > 0 && lastNavLoadedConfig) {
      resetBaseline(lastNavLoadedConfig);
    }
  }, [navLoadToken, lastNavLoadedConfig, resetBaseline]);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    markDirty();
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFrameColorChange = (colorId) => {
    if (!colorId) return;
    savePersistedFrameColors(colorId, colorId, colorId);
    markDirty();
    setConfig((prev) => ({
      ...prev,
      exteriorColor: colorId,
      interiorColor: colorId,
      draaideelColor: colorId,
    }));
  };

  return (
    <ConfiguratorShell
      title={t('configurator.keralitConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <div className="h-full overflow-y-auto p-4 bg-white border-r border-gray-200">
          <KeralitOptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
            onFrameColorChange={handleFrameColorChange}
            additionalItems={config.additionalItems || createDefaultAdditionalItems()}
            onAdditionalItemsChange={handleAdditionalItemsChange}
          />
        </div>
      }
      previewPanel={
        <KeralitDrawingBoard
          width={config.width}
          height={config.height}
          onDimensionsChange={(newWidth, newHeight) => {
            markDirty();
            setConfig((prev) => ({ ...prev, width: newWidth, height: newHeight }));
          }}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={keralitProfileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={[]}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="keralit"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={(savedConfig, options) => {
            if (options?.resetToDefault) {
              handleReset();
              return;
            }
            resetBaseline(savedConfig || config);
          }}
        />
      }
    />
  );
};

export default Keralit;

